import React, {useState} from 'react'
import './CreateBusinessNamePopupScss.scss'
import {Button, Modal, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useDispatch} from 'react-redux'
import {CreateData} from '../BusinessNameSlice/BusinessSlice'
import {KTSVG} from '../../../../../../_metronic/helpers'

function validatePhoneNumber(phone: string) {
  // Step 1: Remove spaces and non-numeric characters
  const sanitizedPhone = phone.replace(/\s+/g, '').trim()

  // Error messages
  if (sanitizedPhone === '') {
    return {
      isValid: false,
      error: 'Phone number is required.',
    }
  }

  // Step 2: Check if all characters are digits
  if (!sanitizedPhone.split('').every((char) => char >= '0' && char <= '9')) {
    return {
      isValid: false,
      error: 'Phone number must contain only digits.',
    }
  }

  // Step 3: Check length (minimum 10 digits)
  if (sanitizedPhone.length < 10) {
    return {
      isValid: false,
      error: 'Phone number must be at least 10 digits long.',
    }
  }

  // Step 4: Check length (reasonable upper limit, e.g., 15 for international numbers)
  if (sanitizedPhone.length > 15) {
    return {
      isValid: false,
      error: 'Phone number cannot exceed 15 digits.',
    }
  }

  // Step 5: Optional country-specific checks (e.g., start with 0 for local numbers)
  if (sanitizedPhone.length === 10 && sanitizedPhone[0] !== '0') {
    return {
      isValid: false,
      error: "Local phone numbers must start with '0'.",
    }
  }

  // If all checks pass, the phone number is valid
  return {
    isValid: true,
    error: null,
  }
}

const CreateBusinessNamePopup = ({show, handleClose}: any) => {
  const dispatch = useDispatch()
  const [step, setStep] = useState(1)
  const [checkBox, setCheckBox] = useState(false)
  const [error, setError] = useState('')
  const [customId, setCustomId] = useState('')
  const [customIdError, setCustomIdError] = useState('')
  const [description, setDescription] = useState('')
  const [descriptionError, setDescriptionError] = useState('')
  const [purposeOfUse, setPurposeOfUse] = useState('')
  const [purposeOfUseError, setPurposeOfUseError] = useState('')
  const [brandOwner, setBrandOwner] = useState<boolean|null>(null)
  const [brandOwnerError, setBrandOwnerError] = useState('')
  const [companyInformation, setCompanyInformation] = useState({
    name: '',
    abn: '',
    website: '',
    address: '',
  })
  const [companyNameError, setCompanyNameError] = useState('')
  const [companyAbnError, setCompanyAbnError] = useState('')
  const [companyWebsiteError, setCompanyWebsiteError] = useState('')
  const [companyAddressError, setCompanyAddressError] = useState('')
  const [requestorContact, setRequestorContact] = useState({
    firstName: '',
    lastName: '',
    position: '',
    phoneNumber: '',
    email: '',
  })
  const [requestorFirstNameError, setRequestorFirstNameError] = useState('')
  const [requestorLastNameError, setRequestorLastNameError] = useState('')
  const [requestorPositionError, setRequestorPositionError] = useState('')
  const [requestorPhoneError, setRequestorPhoneError] = useState('')
  const [requestorEmailError, setRequestorEmailError] = useState('')

  const handleCreate = () => {
    // Collect all form data in backend schema format
    const payload = {
      businessname: customId,
      customerContact: requestorContact.phoneNumber,
      descriptionInternal: description,
      purposeOfUse,
      ownership: brandOwner,
      companyInformation,
      requestorContact,
    }
    dispatch(CreateData(payload))
    handleClose()
    setCustomId('')
    setCustomIdError('')
    setCompanyInformation({ name: '', abn: '', website: '', address: '' })
    setRequestorContact({ firstName: '', lastName: '', position: '', phoneNumber: '', email: '' })
    setCheckBox(false)
    setStep(1)
  }

  const handleSetPhone = (phoneNumber: string) => {
    setError('')
    if (phoneNumber.length === 0) {
      return
    }
    const result = validatePhoneNumber(phoneNumber)
    if (!result.isValid) {
      setError(result.error || '')
    }
  }

  return (
    <>
      <Modal show={show} onHide={handleClose} dialogClassName='business-name-registration-modal modal-dialog-centered mw-600px'>
        <div className='modal-header'>
          <h2 className='fs-2'>New Business Name Registration</h2>
          <div className='btn btn-icon btn-sm' onClick={handleClose}>
            <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
          </div>
        </div>
        <div className='modal-body p-8 px-10'>
          {step === 1 && (
            <div className='step-1'>
              <label className='col-form-label required fw-bold fs-6'>Business Name (Sender ID)</label>
              <input
                value={customId}
                type='text'
                minLength={3}
                maxLength={11}
                name='customId'
                className='form-control form-control-lg form-control-solid mb-3'
                placeholder='Your business name (Sender ID)'
                onChange={e => {
                  const value = e.target.value.replace(/\s+/g, ' ')
                  setCustomId(value)
                  // add in condition to not allow only numbers
                  if (!value) {
                    setCustomIdError('Sender ID is required!')
                  } else if (value.length < 3 || value.length > 11) {
                    setCustomIdError('Sender ID must be between 3 and 11 characters long!')
                  }else if (/^\d+$/.test(value)) {
                    setCustomIdError('Sender ID cannot be numbers only')
                  } else {
                    setCustomIdError('')
                  }
                }}
              />
              {customIdError && <span className='text-danger'>{customIdError}</span>}
              <label className='col-form-label fw-bold fs-6 required'>Description (internal use)</label>
              <textarea
                className='form-control form-control-lg form-control-solid mb-3'
                placeholder='Describe the request (internal use only)'
                value={description}
                onChange={e => {
                  setDescription(e.target.value)
                  if (!e.target.value) {
                    setDescriptionError('Description (internal) is required!')
                  } else {
                    setDescriptionError('')
                  }
                }}
              />
               {descriptionError && <span className='text-danger'>{descriptionError}</span>}
              <label  className='col-form-label fw-bold fs-6'>
                <span className='required'>Purpose of use </span>
              <OverlayTrigger placement='top' overlay={<Tooltip>Please describe the type or message content that you will be sending using this Sender ID eg.  One Time Password, Transactional, Internal communication, Content delivery, Conversational, Marketing</Tooltip>}>
                  <i className='fas fa-exclamation-circle ms-2 fs-7'></i>
              </OverlayTrigger>
              </label>
              <input
                className='form-control form-control-lg form-control-solid mb-3'
                placeholder='Purpose of use (e.g., marketing, conversational, otp)'
                value={purposeOfUse}
                onChange={e => {
                  setPurposeOfUse(e.target.value) 
                  if (!e.target.value) {
                    setPurposeOfUseError('Purpose of use is required!')
                  } else {
                    setPurposeOfUseError('')
                  }
                }}
              />
        
                {purposeOfUseError && <span className='text-danger'>{purposeOfUseError}</span>}
              <label className='col-form-label fw-bold fs-6 required'>Are you the brand owner of the Sender ID?</label>
              <div className='custom-radio-group mb-3'>
                <div className='form-check form-check-custom form-check-solid'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='brandOwner'
                    id='brandOwnerYes'
                    checked={brandOwner === true}
                    onChange={() => {
                      setBrandOwner(true)
                      setBrandOwnerError('')
                    }}
                  />
                  <label className='form-check-label' htmlFor='brandOwnerYes'>Yes</label>
                </div>
                <div className='form-check form-check-custom form-check-solid'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='brandOwner'
                    id='brandOwnerNo'
                    checked={brandOwner === false}
                    onChange={() => {
                      setBrandOwner(false)
                      setBrandOwnerError('')
                    }}
                  />
                  <label className='form-check-label' htmlFor='brandOwnerNo'>No</label>
                </div>
              </div>
              {brandOwner === null && brandOwnerError && <span className='text-danger'>{brandOwnerError}</span>}
              <div className='d-flex justify-content-end mt-10'>
                <button
                  className='custom-btn-purple'
                   disabled={
                     !!customIdError ||
                     !customId ||
                     customId.length < 3 ||
                     customId.length > 11 ||
                     !description ||
                     !purposeOfUse ||
                     brandOwner === null ||
                     description.trim().length === 0 ||
                     purposeOfUse.trim().length === 0 ||
                     !!descriptionError ||
                     !!purposeOfUseError ||
                     brandOwner === null
                   }
                  onClick={() => setStep(2)}
                  type='button'
                >
                  Next
                </button>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className='step-2'>
              <label className='col-form-label required fw-bold fs-6'>Company Name</label>
              <input
                className='form-control form-control-lg form-control-solid mb-3'
                placeholder='Company Name'
                value={companyInformation.name}
                minLength={3}
                maxLength={11}
                onChange={e => {
                  setCompanyInformation({...companyInformation, name: e.target.value})
                  if (!e.target.value) {
                    setCompanyNameError('Company name is required!')
                  }else{
                    setCompanyNameError('')
                  }
                }}
              />
              {companyNameError && <span className='text-danger'>{companyNameError}</span>}
              <label className='col-form-label fw-bold fs-6 required'>ABN</label>
              <input
                className='form-control form-control-lg form-control-solid mb-3'
                placeholder='ABN'
                value={companyInformation.abn}
                onChange={e => {
                  setCompanyInformation({...companyInformation, abn: e.target.value})
                  if (!e.target.value) {
                    setCompanyAbnError('ABN is required!')
                  } else {
                    setCompanyAbnError('')
                  }
                }}
              />
              {companyAbnError && <span className='text-danger'>{companyAbnError}</span>}
              <label className='col-form-label fw-bold fs-6 required'>Website</label>
              <input
                className='form-control form-control-lg form-control-solid mb-3'
                placeholder='Website URL'
                value={companyInformation.website}
                onChange={e => {
                  setCompanyInformation({...companyInformation, website: e.target.value})
                  if (!e.target.value) {
                    setCompanyWebsiteError('Company website is required!')
                  } else {
                    setCompanyWebsiteError('')
                  }
              
                }}
              />
              {companyWebsiteError && <span className='text-danger'>{companyWebsiteError}</span>}
              <label className='col-form-label fw-bold fs-6 required'>Business Address</label>
              <input
                className='form-control form-control-lg form-control-solid mb-3'
                placeholder='Business Address'
                value={companyInformation.address}
                onChange={e => {
                  setCompanyInformation({...companyInformation, address: e.target.value})
                  if (!e.target.value) {
                    setCompanyAddressError('Business address is required!')
                  } else {
                    setCompanyAddressError('')
                  }
                }}
              />
              {companyAddressError && <span className='text-danger'>{companyAddressError}</span>}
              <div className='d-flex justify-content-between mt-10'>
                <button className='custom-btn-cancel' type='button' onClick={() => setStep(1)}>Back</button>
                <button
                  className='custom-btn-purple'
                  disabled={
                    !companyInformation.name ||
                    !companyInformation.abn ||
                    !companyInformation.website ||
                    !companyInformation.address ||
                    !!companyNameError ||
                    !!companyAbnError ||
                    !!companyWebsiteError ||
                    !!companyAddressError
                  }
                  onClick={() => setStep(3)}
                  type='button'
                >
                  Next
                </button>
              </div>
            </div>
          )}
          {step === 3 && (
            <div className='step-3'>
              <label className='col-form-label fw-bold fs-6 required'>Requestor First Name</label>
              <input
                className='form-control form-control-lg form-control-solid mb-3'
                placeholder='First Name'
                value={requestorContact.firstName}
                onChange={e => {
                  setRequestorContact({...requestorContact, firstName: e.target.value})
                  if (!e.target.value) {
                    setRequestorFirstNameError('First name is required!')
                  } else {
                    setRequestorFirstNameError('')
                  }
                }}
              />
              {requestorFirstNameError && <span className='text-danger'>{requestorFirstNameError}</span>}
              <label className='col-form-label fw-bold fs-6 required'>Requestor Last Name</label>
              <input
                className='form-control form-control-lg form-control-solid mb-3'
                placeholder='Last Name'
                value={requestorContact.lastName}
                onChange={e => {
                  setRequestorContact({...requestorContact, lastName: e.target.value})
                  if (!e.target.value) {
                    setRequestorLastNameError('Last name is required!')
                  } else {
                    setRequestorLastNameError('')
                  }
                }}
              />
              {requestorLastNameError && <span className='text-danger'>{requestorLastNameError}</span>}
              <label className='col-form-label fw-bold fs-6 required'>Requestor Position</label>
              <input
                className='form-control form-control-lg form-control-solid mb-3'
                placeholder='Position'
                value={requestorContact.position}
                onChange={e => {
                  setRequestorContact({...requestorContact, position: e.target.value})
                  if (!e.target.value) {
                    setRequestorPositionError('Position is required!')
                  } else {
                    setRequestorPositionError('')
                  }
                }}
              />
              {requestorPositionError && <span className='text-danger'>{requestorPositionError}</span>}
              <label className='col-form-label fw-bold fs-6 required'>Requestor Phone Number</label>
              <input
                className='form-control form-control-lg form-control-solid mb-3'
                placeholder='Phone Number'
                value={requestorContact.phoneNumber}
                onChange={e => {
                  const value = e.target.value
                  setRequestorContact({...requestorContact, phoneNumber: value});
                  const result = validatePhoneNumber(value);
                  if (!result.isValid) {
                    setRequestorPhoneError(result.error || 'Invalid phone number')
                  } else {
                    setRequestorPhoneError('')
                  }
                }}
              />
              {requestorPhoneError && <span className='text-danger'>{requestorPhoneError}</span>}
              <label className='col-form-label fw-bold fs-6 required'>Requestor Email</label>
              <input
                className='form-control form-control-lg form-control-solid mb-3'
                placeholder='Email'
                value={requestorContact.email}
                onChange={e => {
                  const value = e.target.value
                  setRequestorContact({...requestorContact, email: value})
                  const emailRegex = /.+@.+\..+/
                  if (!value) {
                    setRequestorEmailError('Email is required!')
                  } else if (!emailRegex.test(value)) {
                    setRequestorEmailError('Requestor email is invalid!')
                  } else {
                    setRequestorEmailError('')
                  }
                }}
              />
              {requestorEmailError && <span className='text-danger'>{requestorEmailError}</span>}
              <div className='form-check form-check-sm form-check-custom form-check-solid mt-10'>
                <input
                  onChange={() => setCheckBox(!checkBox)}
                  className='form-check-input me-5'
                  id='stagger'
                  type='checkbox'
                  checked={checkBox}
                />
                <div>
                  <label htmlFor='stagger' className=' required'>
                    I confirm that the above information is accurate and I have the authority to submit this request on behalf of the organisation.
                  </label>
                </div>
              </div>
              <div className='d-flex justify-content-between mt-10'>
                <button className='custom-btn-cancel' type='button' onClick={() => setStep(2)}>Back</button>
                <button
                  className='custom-btn-purple'
                  disabled={
                    !requestorContact.firstName ||
                    !requestorContact.lastName ||
                    !requestorContact.position ||
                    !requestorContact.phoneNumber ||
                    !requestorContact.email ||
                    error !== '' ||
                    !checkBox ||
                    !!requestorFirstNameError ||
                    !!requestorLastNameError ||
                    !!requestorPositionError ||
                    !!requestorPhoneError ||
                    !!requestorEmailError
                  }
                  onClick={handleCreate}
                  type='button'
                >
                  Send request
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  )
}

export default CreateBusinessNamePopup
